/**
 * Profile Page Component
 * Handles user profile viewing and editing with responsive design
 */

import React, { useState, useEffect } from 'react';
import { useAuth } from '../../../core/contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { 
  User, 
  Camera, 
  Edit, 
  ChevronRight, 
  Save, 
  Loader, 
  UploadCloud, 
  X,
  Check,
  AlertCircle
} from 'lucide-react';

import ProfileHeader from './ProfileHeader';
import BasicInformation from './BasicInformation';
import AcademicInformation from './AcademicInformation';
import AboutMe from './AboutMe';
import PhotoGallery from './PhotoGallery';
import ProfileSaveHandler from './ProfileSaveHandler';

/**
 * Profile Page Component
 * 
 * @returns {JSX.Element} Profile page component
 */
const ProfilePage = () => {
  const { userProfile, currentUser } = useAuth();
  const navigate = useNavigate();
  const [activeSection, setActiveSection] = useState('basic');
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [profileData, setProfileData] = useState(null);
  const [hasChanges, setHasChanges] = useState(false);

  // Load profile data when component mounts
  useEffect(() => {
    if (userProfile) {
      setProfileData(userProfile);
    }
  }, [userProfile]);

  // Sections for the profile page
  const sections = [
    { id: 'basic', label: 'Basic Information', icon: <User size={20} /> },
    { id: 'academic', label: 'Academic Information', icon: <User size={20} /> },
    { id: 'about', label: 'About Me', icon: <User size={20} /> },
    { id: 'photos', label: 'Photos', icon: <Camera size={20} /> },
  ];

  // Handle section change
  const handleSectionChange = (sectionId) => {
    if (hasChanges) {
      // Show confirmation dialog
      if (window.confirm('You have unsaved changes. Are you sure you want to switch sections?')) {
        setActiveSection(sectionId);
        setHasChanges(false);
      }
    } else {
      setActiveSection(sectionId);
    }
  };

  // Handle edit mode toggle
  const handleEditToggle = () => {
    if (isEditing && hasChanges) {
      // Show confirmation dialog
      if (window.confirm('You have unsaved changes. Are you sure you want to exit edit mode?')) {
        setIsEditing(false);
        setHasChanges(false);
        // Reset to original data
        setProfileData(userProfile);
      }
    } else {
      setIsEditing(!isEditing);
    }
  };

  // Handle profile data changes
  const handleProfileChange = (sectionData, sectionId) => {
    setProfileData(prev => ({
      ...prev,
      [sectionId]: {
        ...prev[sectionId],
        ...sectionData
      }
    }));
    setHasChanges(true);
  };

  // Handle basic info changes
  const handleBasicInfoChange = (data) => {
    handleProfileChange(data, 'basicInfo');
  };

  // Handle academic info changes
  const handleAcademicInfoChange = (data) => {
    handleProfileChange(data, 'academic');
  };

  // Handle bio changes
  const handleBioChange = (data) => {
    handleProfileChange(data, 'bio');
  };

  // Handle photo changes
  const handlePhotoChange = (photos) => {
    setProfileData(prev => ({
      ...prev,
      photos: photos
    }));
    setHasChanges(true);
  };

  // Handle save profile
  const handleSaveProfile = async () => {
    setIsSaving(true);
    setSaveError(null);
    try {
      // Save profile data (implemented in ProfileSaveHandler)
      await ProfileSaveHandler.saveProfile(currentUser.uid, profileData);
      setSaveSuccess(true);
      setHasChanges(false);
      setTimeout(() => {
        setSaveSuccess(false);
      }, 3000);
    } catch (error) {
      console.error('Error saving profile:', error);
      setSaveError('Failed to save profile. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  // Show loading if profile data is not available
  if (!profileData) {
    return (
      <div className="h-screen flex items-center justify-center">
        <Loader className="w-8 h-8 text-green-500 animate-spin" />
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto py-4 px-4 md:py-6 mb-24 md:mb-0">
      {/* Edit Mode Banner */}
      {isEditing && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-3 mb-4 flex items-center justify-between">
          <div className="flex items-center text-green-700">
            <Edit className="w-5 h-5 mr-2 flex-shrink-0" />
            <span className="text-sm md:text-base">You are in <strong>Edit Mode</strong>. Make your changes and click "Save Changes" when you're done.</span>
          </div>
          <button
            onClick={handleEditToggle}
            className="text-green-700 hover:text-green-800 font-medium text-sm flex-shrink-0 ml-2"
          >
            Cancel
          </button>
        </div>
      )}
      
      {/* Profile Header with Edit Button */}
      <div className="relative">
        <ProfileHeader 
          profile={profileData} 
          isEditing={isEditing} 
          onEditToggle={handleEditToggle}
        />
        
        {/* Edit Button - Responsive positioning */}
        {!isEditing && (
          <div className="mt-4 md:absolute md:top-4 md:right-4 md:mt-0">
            <button
              onClick={handleEditToggle}
              className="flex items-center justify-center space-x-2 px-4 py-2 bg-green-500 text-white rounded-lg shadow-sm hover:bg-green-600 transition-colors w-full md:w-auto"
            >
              <Edit size={18} />
              <span>Edit Profile</span>
            </button>
          </div>
        )}
      </div>

      {/* Help tip when not editing */}
      {!isEditing && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-6 mt-4 flex items-start">
          <div className="flex items-start text-blue-700 text-sm">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <span>This is how others see your profile. Click the <strong>Edit Profile</strong> button to make changes.</span>
          </div>
        </div>
      )}

      <div className="mt-8 grid grid-cols-1 md:grid-cols-4 gap-6">
        {/* Section Navigation - Horizontal scroll on mobile */}
        <div className="md:col-span-1">
          {/* Mobile horizontal navigation */}
          <div className="md:hidden mb-6">
            <div className="bg-white rounded-xl shadow-sm overflow-hidden">
              <div className="p-3 border-b border-gray-100">
                <h3 className="font-semibold text-gray-800 text-sm">Profile Sections</h3>
              </div>
              <div className="overflow-x-auto">
                <div className="flex space-x-2 p-3">
                  {sections.map((section) => (
                    <button
                      key={section.id}
                      onClick={() => handleSectionChange(section.id)}
                      className={`flex items-center space-x-2 px-3 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-colors ${
                        activeSection === section.id
                          ? 'bg-green-500 text-white'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                    >
                      {section.icon}
                      <span>{section.label}</span>
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Desktop sidebar navigation */}
          <div className="hidden md:block bg-white rounded-xl shadow-sm overflow-hidden">
            <div className="p-4 border-b border-gray-100">
              <h3 className="font-semibold text-gray-800">Profile Sections</h3>
            </div>
            <nav>
              <ul>
                {sections.map((section) => (
                  <li key={section.id}>
                    <button
                      onClick={() => handleSectionChange(section.id)}
                      className={`w-full text-left py-3 px-4 flex items-center justify-between transition-colors ${
                        activeSection === section.id
                          ? 'bg-green-50 text-green-500 font-medium'
                          : 'text-gray-700 hover:bg-gray-50'
                      }`}
                    >
                      <div className="flex items-center space-x-3">
                        {section.icon}
                        <span>{section.label}</span>
                      </div>
                      <ChevronRight size={16} className={activeSection === section.id ? 'text-green-500' : 'text-gray-400'} />
                    </button>
                  </li>
                ))}
              </ul>
            </nav>

            {/* Completion Status */}
            <div className="p-4 border-t border-gray-100">
              <div className="mb-2 flex items-center justify-between">
                <h3 className="text-sm font-medium text-gray-700">Profile Completion</h3>
                <span className="text-sm font-medium text-green-500">70%</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2.5">
                <div className="bg-green-500 h-2.5 rounded-full" style={{ width: '70%' }}></div>
              </div>
              <p className="mt-2 text-xs text-gray-500">
                Complete your profile to connect with more students
              </p>
            </div>
          </div>
        </div>

        {/* Content Section */}
        <div className="md:col-span-3">
          <div className="bg-white rounded-xl shadow-sm overflow-hidden">
            {/* Section Header */}
            <div className="p-4 border-b border-gray-100 flex items-center justify-between">
              <h2 className="font-semibold text-gray-800">
                {sections.find(s => s.id === activeSection)?.label}
              </h2>
              
              {/* Edit Mode Indicator */}
              {isEditing && (
                <div className="text-sm text-green-600 flex items-center">
                  <Edit size={16} className="mr-1" />
                  <span className="hidden sm:inline">Editing Mode</span>
                </div>
              )}
            </div>
            
            {/* Save Notification */}
            {saveSuccess && (
              <div className="bg-green-50 text-green-700 p-3 flex items-center">
                <Check size={16} className="mr-2 flex-shrink-0" />
                <span>Profile saved successfully</span>
              </div>
            )}
            
            {saveError && (
              <div className="bg-red-50 text-red-700 p-3 flex items-center">
                <AlertCircle size={16} className="mr-2 flex-shrink-0" />
                <span>{saveError}</span>
              </div>
            )}

            {/* Section Content */}
            <div className="p-4">
              {activeSection === 'basic' && (
                <BasicInformation 
                  data={profileData.basicInfo} 
                  isEditing={isEditing}
                  onChange={handleBasicInfoChange}
                />
              )}
              
              {activeSection === 'academic' && (
                <AcademicInformation 
                  data={profileData.academic} 
                  isEditing={isEditing}
                  onChange={handleAcademicInfoChange}
                />
              )}
              
              {activeSection === 'about' && (
                <AboutMe 
                  data={profileData.bio} 
                  isEditing={isEditing}
                  onChange={handleBioChange}
                />
              )}
              
              {activeSection === 'photos' && (
                <PhotoGallery 
                  photos={profileData.photos || []} 
                  isEditing={isEditing}
                  onChange={handlePhotoChange}
                  userId={currentUser.uid}
                />
              )}
            </div>
          </div>
        </div>
      </div>
      
      {/* Save Changes Button - Responsive positioning */}
      {isEditing && (
        <>
          {/* Mobile: Fixed at bottom with proper spacing */}
          <div className="md:hidden fixed bottom-20 left-0 right-0 bg-white border-t border-gray-200 p-4 z-30">
            <button
              onClick={handleSaveProfile}
              disabled={isSaving || !hasChanges}
              className={`flex items-center justify-center space-x-2 py-3 px-6 rounded-xl font-medium w-full ${
                isSaving || !hasChanges
                  ? 'bg-gray-200 text-gray-500 cursor-not-allowed'
                  : 'bg-green-500 text-white hover:bg-green-600'
              }`}
            >
              {isSaving ? (
                <>
                  <Loader size={20} className="animate-spin" />
                  <span>Saving Profile...</span>
                </>
              ) : (
                <>
                  <Save size={20} />
                  <span>{hasChanges ? 'Save Changes' : 'No Changes to Save'}</span>
                </>
              )}
            </button>
          </div>

          {/* Desktop: Fixed at bottom */}
          <div className="hidden md:block fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 p-4 flex justify-center z-30">
            <button
              onClick={handleSaveProfile}
              disabled={isSaving || !hasChanges}
              className={`flex items-center justify-center space-x-2 py-3 px-6 rounded-xl font-medium w-full max-w-md ${
                isSaving || !hasChanges
                  ? 'bg-gray-200 text-gray-500 cursor-not-allowed'
                  : 'bg-green-500 text-white hover:bg-green-600'
              }`}
            >
              {isSaving ? (
                <>
                  <Loader size={20} className="animate-spin" />
                  <span>Saving Profile...</span>
                </>
              ) : (
                <>
                  <Save size={20} />
                  <span>{hasChanges ? 'Save Changes' : 'No Changes to Save'}</span>
                </>
              )}
            </button>
          </div>
        </>
      )}
    </div>
  );
};

export default ProfilePage;